import { Formik } from 'formik'
import { useIntl, FormattedMessage, defineMessages } from 'react-intl'
import { NavLink } from 'react-router-dom'
import { isEmpty } from 'lodash'

import { Button } from 'components/v2/Button'
import { Input } from 'components/Input'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import { Row } from '@10xLabs/react-web/components/Row'
import { Column } from '@10xLabs/react-web/components/Column'
import Heading from 'components/v2/Heading'
import FormLayout from 'features/App/components/FormLayout'
import { CityAutocomplete } from 'features/Cities/components/CityAutocomplete'
import { CompanyAutocomplete } from 'features/Companies/components/CompanyAutocomplete'
import { SelectType } from 'features/Inventories/components/SelectType'
import { schema } from './schema'
import { getUnprocessableErrors } from 'utils/error'
import { MultiUserAutocomplete } from 'features/Users/components/MultiUserAutocomplete'
import { Label } from 'components/v2/Label'
import { GTUserSuggestion } from 'gql/types.generated'

const messages = defineMessages({
  companyPlaceholder: {
    id: 'spanish.dictionary.selectCompany',
    defaultMessage: 'Seleccionar compañía'
  },
  namePlaceholder: {
    id: 'spanish.dictionary.name',
    defaultMessage: 'Nombre'
  },
  line1Placeholder: {
    id: 'spanish.dictionary.line1',
    defaultMessage: 'Calle y número'
  },
  line2Placeholder: {
    id: 'spanish.dictionary.line2',
    defaultMessage: 'Additional information'
  },
  zipCodePlaceholder: {
    id: 'spanish.dictionary.zipCode',
    defaultMessage: 'Código postal'
  },
  cityPlaceholder: {
    id: 'spanish.dictionary.city',
    defaultMessage: 'Ciudad'
  }
})

const NewInventoryForm = (props: TSFixMe): TSFixMe => {
  const {
    values,
    touched,
    errors,
    handleChange,
    setFieldValue,
    setFieldTouched,
    handleBlur,
    handleSubmit,
    error,
    loading,
    isEdit,
    showCompany
  } = props

  const intl = useIntl()

  const handleCityChange = (value: TSFixMe): TSFixMe => {
    setFieldValue('address.city', value)
  }

  return (
    <form autoComplete="off" onSubmit={handleSubmit}>
      <FormLayout.CardContent>
        <Row>
          <Column>
            <Heading.H5>
              <FormattedMessage id="spanish.inventories.form.general.title" defaultMessage="General" />
            </Heading.H5>
          </Column>
        </Row>

        {showCompany && (
          <Row>
            <Column width="50%">
              <CompanyAutocomplete
                id="company.id"
                onChange={(value: TSFixMe): TSFixMe => {
                  setFieldTouched('company', true)
                  setFieldValue('company', value)
                  setFieldValue('users', [])
                }}
                onBlur={handleBlur}
                value={values.company.name}
                placeholder={intl.formatMessage(messages.companyPlaceholder)}
                touched={touched.company && touched.company.id}
                errors={errors.company && errors.company.id}
                label={<FormattedMessage id="spanish.dictionary.company" defaultMessage="Compañía" />}
              />
            </Column>
          </Row>
        )}

        <Row>
          <Column width="50%">
            <Input
              id="name"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.name}
              placeholder={intl.formatMessage(messages.namePlaceholder)}
              touched={touched.name}
              errors={errors.name}
              label={<FormattedMessage id="spanish.dictionary.name" defaultMessage="Nombre" />}
            />
          </Column>
        </Row>

        <Row>
          <Column width="50%">
            <SelectType
              id="type"
              value={values.type}
              onChange={handleChange}
              onBlur={handleBlur}
              touched={touched.type}
              errors={errors.type}
              label={<FormattedMessage id="spanish.inventories.type.label" defaultMessage="Tipo de almacén" />}
              disabled={isEdit}
              readOnly={isEdit}
            />
          </Column>
        </Row>

        <Row>
          <Column>
            <Heading.H5>
              <FormattedMessage id="spanish.inventories.form.address.title" defaultMessage="Dirección" />
            </Heading.H5>
          </Column>
        </Row>

        <Row>
          <Column width="50%">
            <Input
              id="address.line1"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.address.line1}
              placeholder={intl.formatMessage(messages.line1Placeholder)}
              touched={touched.address && touched.address.line1}
              errors={errors.address && errors.address.line1}
              label={<FormattedMessage id="spanish.inventories.address.line1.label" defaultMessage="Calle y número" />}
            />
          </Column>

          <Column width="50%">
            <Input
              id="address.line2"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.address.line2}
              placeholder={intl.formatMessage(messages.line2Placeholder)}
              touched={touched.address && touched.address.line2}
              errors={errors.address && errors.address.line2}
              label={
                <FormattedMessage
                  id="spanish.inventories.address.line2.label"
                  defaultMessage="Adicional información <small>(Opcional)</small>"
                  values={{
                    small: (msg): TSFixMe => <small>{msg}</small>
                  }}
                />
              }
            />
          </Column>
        </Row>

        <Row>
          <Column width="50%">
            <CityAutocomplete
              id="address.city.id"
              value={values.address.city.name}
              onChange={handleCityChange}
              onBlur={handleBlur}
              placeholder={intl.formatMessage(messages.cityPlaceholder)}
              touched={touched.address && touched.address.city && touched.address.city.id}
              errors={errors.address && errors.address.city && errors.address.city.id}
              label={<FormattedMessage id="places.new.form.city.label" defaultMessage="City" />}
            />
          </Column>

          <Column width="50%">
            <Input
              id="address.zipCode"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.address.zipCode}
              placeholder={intl.formatMessage(messages.zipCodePlaceholder)}
              touched={touched.address && touched.address.zipCode}
              errors={errors.address && errors.address.zipCode}
              label={<FormattedMessage id="spanish.inventories.address.zipCode.label" defaultMessage="Código postal" />}
            />
          </Column>
        </Row>

        <Row>
          <Column>
            <Heading.H5>
              <FormattedMessage id="spanish.dictionary.users" defaultMessage="Usuarios" />
            </Heading.H5>
          </Column>
        </Row>

        <Row>
          <Column>
            {values.company.id && values.type ? (
              <MultiUserAutocomplete
                id="users"
                type={values.type}
                selectedUsers={values.users}
                companyId={values.company.id}
                onChange={(newUsers: GTUserSuggestion[]): void => {
                  setFieldValue('users', newUsers)
                }}
                onBlur={handleBlur}
                isAssigned={false}
                label={<FormattedMessage id="spanish.inventories.form.users.title" defaultMessage="Responsables" />}
              />
            ) : (
              <>
                <Label>
                  <FormattedMessage id="spanish.inventories.form.users.title" defaultMessage="Responsables" />
                </Label>
                <FormattedMessage
                  id="spanish.dictionary.selectCompany"
                  defaultMessage="Seleccionar compañía y tipo de usuario"
                />
              </>
            )}
          </Column>
        </Row>
      </FormLayout.CardContent>

      <FormLayout.Split />

      <FormLayout.CardFooter>
        {error && (
          <div>
            <ErrorMessage errors={getUnprocessableErrors(error)}>
              <FormattedMessage
                id="inventories.form.error"
                defaultMessage="There was an error while creating your inventory"
              />
            </ErrorMessage>
          </div>
        )}

        <FormLayout.CardFooterActions>
          <Button as={NavLink} to="/inventories" kind="cancel">
            <FormattedMessage id="spanish.dictionary.cancel" defaultMessage="Cancelar" />
          </Button>

          <Button type="submit" kind="primary" loading={loading}>
            {isEdit ? (
              <FormattedMessage id="spanish.dictionary.edit" defaultMessage="Editar" />
            ) : (
              <FormattedMessage id="spanish.dictionary.create" defaultMessage="Crear" />
            )}
          </Button>
        </FormLayout.CardFooterActions>
      </FormLayout.CardFooter>
    </form>
  )
}

const EMPTY_ADDRESS = {
  line1: '',
  line2: '',
  zipCode: '',
  city: { id: '', name: '' }
}
export const NewInventory = ({ inventory = {}, showCompany, onSubmit, error, loading, isEdit }: TSFixMe): TSFixMe => {
  const intl = useIntl()

  const initialInventory = {
    name: inventory.name || '',
    company: inventory.company || { name: '', id: '' },
    type: inventory.type || '',
    address: isEmpty(inventory.address)
      ? EMPTY_ADDRESS
      : {
          line1: inventory.address.line1 || '',
          line2: inventory.address.line2 || '',
          city: isEmpty(inventory.address.city) ? { id: '', name: '' } : inventory.address.city,
          zipCode: inventory.address.zipCode || ''
        },
    users: inventory.users || []
  }

  return (
    <Formik initialValues={initialInventory} validationSchema={schema(intl, isEdit)} onSubmit={onSubmit}>
      {(props): TSFixMe => {
        return <NewInventoryForm {...props} showCompany={showCompany} isEdit={isEdit} error={error} loading={loading} />
      }}
    </Formik>
  )
}
